import { Link, useRouterState } from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import { cx } from "@/lib/cx";
import { useThemeToggle } from "@/lib/theme";
import { useOpenAddModel } from "@/modules/plus/data/components/AddModelDialog";
import { atoms } from "@/state";

const NavLink: React.FC<{ to: string; label: string }> = ({ to, label }) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={cx(
        "rounded-md px-3 py-1.5 text-sm font-medium transition",
        active
          ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
          : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800",
      )}
    >
      {label}
    </Link>
  );
};

// Sun / moon icons rendered inline so we don't pull in a library for two glyphs.
const SunIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

// Split out so the whole Toolbar doesn't re-render when the orphan list changes,
// and so signed-out users don't subscribe to the (auth-gated) photos query.
const OrphanPhotosLink: React.FC = () => {
  const orphans = useAtomValue(atoms.photos.orphans);
  if (orphans.length === 0) {
    return null;
  }
  return (
    <Link
      to="/admin/orphan-photos"
      className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/70"
      title="Photos whose code doesn't attach to any model"
    >
      Orphan photos
      <span className="rounded-full bg-amber-200 px-1.5 py-px text-[10px] font-semibold text-amber-900 dark:bg-amber-800/60 dark:text-amber-100">
        {orphans.length}
      </span>
    </Link>
  );
};

export const Toolbar: React.FC = () => {
  const email = useAtomValue(atoms.auth.email);
  const signedIn = useAtomValue(atoms.auth.isSignedIn);
  const signOut = useSetAtom(atoms.auth.signOut);
  const openReport = useSetAtom(atoms.reports.open);
  const openAddModel = useOpenAddModel();
  const { mounted, effective, toggle } = useThemeToggle();

  return (
    <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center gap-5">
        <Link
          to="/"
          className="flex items-baseline gap-2 text-gray-900 transition hover:text-blue-600 dark:text-gray-100 dark:hover:text-blue-400"
        >
          <span className="text-base font-semibold tracking-tight">Product Lookup System</span>
          <span className="rounded bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            PLUS
          </span>
        </Link>
        <nav className="flex items-center gap-1">
          <NavLink to="/" label="Search" />
          <NavLink to="/gallery" label="Gallery" />
          <a
            href="/reference.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Reference (PDF)
          </a>
        </nav>
      </div>
      <div className="flex items-center gap-3">
        {signedIn && <OrphanPhotosLink />}
        {signedIn && (
          <button
            type="button"
            onClick={() => openAddModel()}
            className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Add model
          </button>
        )}
        <button
          type="button"
          onClick={() => openReport()}
          className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          Report issue
        </button>
        {email && <span className="text-xs text-gray-500 dark:text-gray-400">{email}</span>}
        <button
          type="button"
          onClick={toggle}
          // The pre-paint script applies the theme class before React renders,
          // so any label derived from `effective` must wait for the first
          // mount to be sure it matches. Use a generic label until then.
          title={
            mounted
              ? effective === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
              : "Toggle theme"
          }
          aria-label={
            mounted
              ? effective === "dark"
                ? "Switch to light mode"
                : "Switch to dark mode"
              : "Toggle theme"
          }
          className="grid h-7 w-7 place-items-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          {mounted ? effective === "dark" ? <SunIcon /> : <MoonIcon /> : null}
        </button>
        <button
          type="button"
          onClick={() => signOut()}
          className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          Sign out
        </button>
      </div>
    </header>
  );
};
